// ═══════════════════════════════════════════════════════════════
// RiskPortal1 — Role-Based Access Control (single source of truth)
// ---------------------------------------------------------------
// Roles are DYNAMIC: an admin can create new ones at runtime. So the live
// registry is BUILT FROM THE BACKEND (`risk_portal_workflow_config()` →
// `role_config`, a map of roleKey → { permissions, views, label }), with the
// static map below acting only as a seed/fallback for the built-in roles and
// as a template for any custom role that has no explicit config yet.
//
// Components consume this via the <RbacProvider> + useRbac() hook and gate on
// CAPABILITIES (can('can_create')) or VIEWS (canView('risk')) — never on role
// names — so a newly-created role "just works" the moment it exists in
// role_config, with no code change here.
//
// Security note: this is UX gating only. The backend MUST independently
// enforce every permission — hiding a button never secures an endpoint.
// ═══════════════════════════════════════════════════════════════

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { risk_portal_workflow_config, risk_portal_roles_list } from '../../services';
import { currentUserEmail } from './workflow';

// Unwrap the SbomApiIntercepter envelope — same helper the portal Forms use.
const extract = (res) =>
    res?.data?.response_data?.data || res?.data?.data ||
    res?.statusCode?.response_data?.data || res?.statusCode?.data || null;

// Canonical role keys for the BUILT-IN roles. Custom roles are just extra
// string keys that arrive from the backend — they don't need an entry here.
export const ROLES = {
    ADMIN: 'risk_admin',
    MANAGER: 'risk_manager',
    OWNER: 'risk_owner',
    REVIEWER: 'risk_reviewer',
    BUSINESS_OWNER: 'business_owner',
    COMMITTEE: 'risk_committee',
    CISO: 'ciso',
};

// The full catalogue of capability flags. A role editor UI can render one
// toggle per key; the registry guarantees every role has all of them (unknown
// → false), so a check is never undefined.
export const PERMISSION_KEYS = ['can_configure', 'can_create', 'can_assess', 'can_treat', 'can_approve'];

// The full catalogue of nav views. A role editor UI can offer these as the
// checkbox list of "which pages can this role open".
export const PORTAL_VIEWS = ['home', 'assets', 'my_risks', 'risk', 'heatmap', 'treatments', 'my_treatments', 'kris', 'scenarios', 'nova_ai', 'compliance', 'reports', 'guide', 'users', 'business_units', 'projects', 'categories', 'workflow'];

// Every capability defaults to false — you must opt IN. Spread this into a
// role so a typo or a brand-new permission key can't accidentally grant access.
const NO_PERMS = PERMISSION_KEYS.reduce((acc, k) => ({ ...acc, [k]: false }), {});

// Built-in role seed. Each: { label, views, permissions }. The backend
// role_config overlays on top of this (see buildRegistry).
//
// The `views` per role mirror the reference portal's per-role sidebar
// (DEFAULT_ROLE_CONFIGS in src/Pages/RiskPortal/RiskPortal.js), mapped to this
// app's nav ids: pm_home/dashboard/executive_dashboard → home; risks/my_risks/
// bu_risks → risk; compliance/reports/users/business_units/projects/categories/
// appetite/workflow map 1:1. Reference-only modules with no page here (heatmap,
// kris, treatments, reviews, scenarios, nova_ai, guide, audit_log, settings)
// are omitted, as is 'assets' (not a risk-portal module in the reference).
export const DEFAULT_ROLE_CONFIG = {
    [ROLES.ADMIN]: {
        label: 'Risk Admin',
        // Reference admin sidebar: Administration + config screens. 'risk' is
        // added so the shared home dashboard's "View all → Risk Register" works.
        // 'guide' (RBAC Guide) is a read-only reference every role can open.
        views: ['home', 'risk', 'users', 'business_units', 'projects', 'workflow', 'categories',  'compliance', 'guide'],
        permissions: { can_configure: true, can_create: true, can_assess: true, can_treat: true, can_approve: true },
    },
    [ROLES.MANAGER]: {
        label: 'Risk Manager',
        // Reference manager sidebar: dashboard, risks, heatmap, kris, treatments,
        // appetite, compliance, scenarios, nova_ai. Business Units + Projects are
        // deliberately admin-only (see risk_admin views), so they're omitted here.
        views: ['home', 'risk', 'treatments',  'compliance'],
        permissions: { ...NO_PERMS, can_create: true, can_assess: true, can_treat: true },
    },
    [ROLES.OWNER]: {
        label: 'Risk Owner',
        // Owners work their personal queue ("My Risks"), not the org-wide Risk
        // Register — so 'risk' is intentionally omitted. My Risks already shows
        // every risk they own (with an Action-required / Awaiting status per the
        // workflow stage), which is all an owner needs.
        views: ['home', 'my_risks', 'my_treatments', 'guide'],
        permissions: { ...NO_PERMS, can_create: true, can_treat: true },
    },
    [ROLES.REVIEWER]: {
        label: 'Risk Reviewer',
        // Reference: dashboard, pending_reviews, risks, heatmap.
        views: ['home', 'risk', 'guide'],
        permissions: { ...NO_PERMS, can_approve: true },
    },
    [ROLES.BUSINESS_OWNER]: {
        label: 'Business Owner',
        // Reference: dashboard, pending_acceptance, bu_risks, heatmap.
        views: ['home', 'risk', 'guide'],
        permissions: { ...NO_PERMS, can_approve: true },
    },
    [ROLES.COMMITTEE]: {
        label: 'Risk Committee',
        // Reference: dashboard, pending_acceptance, risks, heatmap.
        views: ['home', 'risk', 'guide'],
        permissions: { ...NO_PERMS, can_approve: true },
    },
    [ROLES.CISO]: {
        label: 'CISO',
        // Reference: executive_dashboard, risks, heatmap, kris, appetite, compliance, audit_log, reports.
        views: ['home', 'risk', 'appetite', 'compliance', 'reports', 'guide'],
        permissions: { ...NO_PERMS }, // executive read-only
    },
};

// Template applied to a CUSTOM role that exists in role_config but has no
// static entry — least privilege: sees only the dashboard, can do nothing,
// until the admin grants views/permissions.
const GENERIC_ROLE = { label: '', views: ['home'], permissions: { ...NO_PERMS } };

// Least-privileged built-in — the safe fallback for an unknown/missing role.
export const FALLBACK_ROLE = ROLES.OWNER;

// 'risk_champion' → 'Risk Champion' — a readable label for a custom role key.
export const humanizeRole = (key = '') =>
    String(key).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();

// Build the LIVE registry: static defaults, then overlay whatever the backend
// stores in role_config. Any custom key in role_config (not '_settings' etc.)
// becomes a first-class role built from GENERIC_ROLE + its stored config — so
// admin-created roles appear here automatically.
export const buildRegistry = (serverRoleConfig) => {
    const registry = {};
    for (const [key, cfg] of Object.entries(DEFAULT_ROLE_CONFIG)) {
        registry[key] = { ...cfg, permissions: { ...cfg.permissions } };
    }
    for (const [key, sc] of Object.entries(serverRoleConfig || {})) {
        if (!key || key.startsWith('_')) continue; // skip _settings and other non-role keys
        const base = registry[key] || { ...GENERIC_ROLE, label: humanizeRole(key) };
        registry[key] = {
            label: sc.label || base.label || humanizeRole(key),
            // Union code defaults with backend views so a newly added code
            // default (e.g. the admin-only 'users' nav) is NEVER hidden by a
            // stale stored config. The backend can ADD views, not remove
            // code-defined ones — mirrors the reference portal, which treats the
            // sidebar as code-defined rather than fully customer-configurable.
            views: Array.from(new Set([
                ...(base.views || []),
                ...(Array.isArray(sc.views) ? sc.views : []),
            ])),
            permissions: { ...base.permissions, ...(sc.permissions || {}) },
        };
    }
    return registry;
};

// Resolve the current user's role key against a live registry. Supports a
// dev/testing override (?role=… in the URL, or localStorage 'rp1_role') so all
// roles are easy to preview before the real auth wiring lands.
export const resolveRole = (registry) => {
    try {
        // 1) Dev/testing override — ?role=… or localStorage 'rp1_role'.
        const params = new URLSearchParams(window.location.search);
        const dev = params.get('role') || window.localStorage.getItem('rp1_role');
        if (dev && registry[dev]) return dev;

        // 2) The authoritative source is `risk_role` — the portal role the LOGIN
        //    API returns, refreshed on every sign-in, so it always reflects the
        //    user's current assignment. It MUST win over `risk_portal_user.role`,
        //    which is only a cache written when the portal was last opened and
        //    can go stale (e.g. an earlier admin session, or an ensure-admin
        //    default) — reading the cache first would shadow a reassigned role
        //    (e.g. a risk_manager still seeing the risk_admin dashboard).
        const riskRole = window.localStorage.getItem('risk_role');
        if (riskRole && registry[riskRole]) return riskRole;

        // 3) Fall back to the cached portal user — this is the ONLY source for a
        //    portal-form login (RiskPortalAuth), which has no dashboard risk_role.
        try {
            const portalUser = JSON.parse(window.localStorage.getItem('risk_portal_user') || '{}');
            if (portalUser?.role && registry[portalUser.role]) return portalUser.role;
        } catch { /* malformed — fall through */ }

        // 4) Fall back to the generic user profile, if it carries a portal role.
        const profile = JSON.parse(window.localStorage.getItem('user_profile') || '{}');
        const role = profile?.risk_portal_role || profile?.portal_role || profile?.role;
        if (role && registry[role]) return role;
    } catch {
        /* malformed profile / sandboxed env — fall through */
    }
    return registry[FALLBACK_ROLE] ? FALLBACK_ROLE : Object.keys(registry)[0];
};

// ─── Context + provider ────────────────────────────────────────
const RbacContext = createContext(null);

// Wrap the portal once. It loads the role registry from the backend on mount
// (degrading to static defaults if the call fails, e.g. no session in dev),
// resolves the current role, and exposes can()/canView() plus refresh() so an
// admin's "create role" action can re-pull the registry.
export function RbacProvider({ children, roleOverride }) {
    // Seed synchronously with static defaults so the first paint is never blank.
    const [registry, setRegistry] = useState(() => buildRegistry(null));
    // The org's ordered workflow stages (id, name, owner_role, next_stages…).
    // Consumed by the register/dashboard visibility scoping; empty until the
    // config loads, where helpers fall back to the default blueprint.
    const [stages, setStages] = useState([]);
    const [loading, setLoading] = useState(true);
    // The signed-in user's role as reported by the backend roles list, matched by
    // email. This is the authoritative source (the login `risk_role` can be stale
    // or absent for a portal-form login), so when present it wins over resolveRole.
    const [apiRole, setApiRole] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = extract(await risk_portal_workflow_config());
            setRegistry(buildRegistry(data?.role_config));
            setStages(Array.isArray(data?.stages)
                ? [...data.stages].sort((a, b) => (a.order || 0) - (b.order || 0))
                : []);
        } catch {
            /* keep static defaults — an org with no config still works */
        } finally {
            setLoading(false);
        }
    }, []);

    // Pull the org's roles list and find the current user's row by email. On a
    // match we cache the role into `risk_role` so resolveRole() and every other
    // screen stay in sync, and surface it via `apiRole` for the live `role` memo.
    // On no match / error we leave the existing resolution untouched.
    const refreshMyRole = useCallback(async () => {
        try {
            const list = extract(await risk_portal_roles_list())?.roles || [];
            const email = currentUserEmail();
            const mine = list.find((r) => String(r.email || '').toLowerCase() === email);
            if (mine?.role) {
                window.localStorage.setItem('risk_role', mine.role);
                setApiRole(mine.role);
            }
        } catch {
            /* keep whatever resolveRole() finds from localStorage */
        }
    }, []);

    useEffect(() => { refresh(); refreshMyRole(); }, [refresh, refreshMyRole]);

    const role = useMemo(
        () => roleOverride || (apiRole && registry[apiRole] ? apiRole : resolveRole(registry)),
        [roleOverride, apiRole, registry]
    );
    const config = registry[role] || registry[FALLBACK_ROLE] || Object.values(registry)[0];

    const value = useMemo(() => ({
        loading,
        registry,                       // full role map — for an admin roles-management screen
        roleKeys: Object.keys(registry),
        role,
        config,
        stages,                         // ordered workflow stages — for visibility scoping
        can: (permission) => Boolean(config?.permissions?.[permission]),
        canView: (viewId) => Boolean(config?.views?.includes(viewId)),
        refresh,
    }), [loading, registry, role, config, stages, refresh]);

    return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
}

// The hook every RiskPortal1 component uses.
//   const { config, can, canView } = useRbac();
//   {can('can_create') && <NewRiskButton />}
export const useRbac = () => {
    const ctx = useContext(RbacContext);
    if (!ctx) throw new Error('useRbac must be used within an <RbacProvider>');
    return ctx;
};

// ─── <Can> — declarative gate for ANY chunk of UI ──────────────
// Not just buttons: wrap a whole section, card, column, table — anything —
// and it renders only when the current role passes the check. Combine props;
// all supplied conditions must hold (AND):
//   permission — a single capability            <Can permission="can_treat">
//   allOf      — every capability in the list   <Can allOf={['can_assess','can_treat']}>
//   anyOf      — at least one capability         <Can anyOf={['can_create','can_treat']}>
//   view       — a nav view id                   <Can view="compliance">
//   fallback   — what to render when denied (default: nothing)
//
//   <Can anyOf={['can_create', 'can_treat']}>
//     <section className="rp1_panel">…Quick Actions…</section>
//   </Can>
export function Can({ permission, allOf, anyOf, view, fallback = null, children }) {
    const { can, canView } = useRbac();
    let ok = true;
    if (permission) ok = ok && can(permission);
    if (allOf) ok = ok && allOf.every(can);
    if (anyOf) ok = ok && anyOf.some(can);
    if (view) ok = ok && canView(view);
    return ok ? <>{children}</> : fallback;
}
